/**
 * The framework-agnostic core of "does this game session need a networked
 * sim, and if so, run it" — everything GameContainer.tsx used to decide and
 * manage inline across two `useEffect`s. Pulling it out here means the
 * actual decision logic (guard conditions, seeding, dedup-by-reference on
 * incoming room state, teardown) is testable with plain function calls, no
 * React rendering involved — GameContainer's own job shrinks to: build a
 * NetworkedSimBindingInputs from its props on mount/dep-change, hand it to
 * makeNetworkedSimBinding, and forward gameState updates + getLiveGameState
 * through. See realtimeNetworkedSim.ts for why the sim itself can't just be
 * driven by relayed room moves.
 */
import { makeRealtimeNetworkedSim } from './realtimeNetworkedSim'
import type { NetworkedSimBinding, NetworkedSimBindingInputs } from './networkedSimBinding.types'

const INACTIVE_BINDING: NetworkedSimBinding<any> = {
  getLiveGameState: undefined,
  onRoomStateUpdate: () => {},
  stop: () => {},
}

export function makeNetworkedSimBinding<G>(inputs: NetworkedSimBindingInputs<G>): NetworkedSimBinding<G> {
  const { isNetworked, checkpointState, gameState, mySeat, realtime } = inputs
  const createPlugin = realtime?.createPlugin
  if (!isNetworked || !checkpointState || gameState == null || !createPlugin || !realtime?.tickRateHz) {
    return INACTIVE_BINDING
  }

  // Seeded once from whatever gameState is available right now — never
  // re-seeded just because a later gameState arrives (that would discard
  // local tick progress on every room update). Later updates instead flow
  // in through onRoomStateUpdate below, exactly like ordinary discrete
  // moves/checkpoints should.
  const sim = makeRealtimeNetworkedSim(gameState, realtime.tickRateHz, checkpointState, mySeat, createPlugin())
  sim.start()
  let lastSeenGameState = gameState

  return {
    getLiveGameState: () => sim.getG(),
    onRoomStateUpdate(nextGameState) {
      if (nextGameState == null || nextGameState === lastSeenGameState) return
      lastSeenGameState = nextGameState
      sim.applyIncomingState(nextGameState)
    },
    stop: () => sim.stop(),
  }
}
